/**
 * <mb-crisp-launcher> — our own launcher for the Crisp chatbox.
 * ─────────────────────────────────────────────────────────────────────────────
 * A copy of <mb-support-launcher> (Freshchat), which is left as it was. The
 * differences are what Crisp makes possible:
 *
 *   • Crisp renders inline (.crisp-client, no iframe) and has a real event
 *     API, so the stock bubble is hidden with `do chat:hide` and this pill
 *     takes over: `chat:show` + `chat:open` on press, `chat:hide` again when
 *     the box closes.
 *   • Unread count is read back from `chat:unread:count` after every
 *     `message:received` and painted on the seal.
 *   • The presence pip is a real claim here: green while an agent is on
 *     (`is('website:available')`), grey otherwise — kept fresh through the
 *     `website:availability:changed` relay.
 *   • An agent's message, arriving while the box is closed, is shown in our
 *     own preview card (name, text, Reply, dismiss) instead of Crisp's popup.
 *     It auto-hides after data-preview-timeout ms — paused while hovered or
 *     focused — and the badge keeps the count after it goes.
 *
 * The chatbox is loaded by the Crisp Shopify app embed, so nothing is fetched
 * here — but the pill ships `hidden` and is revealed the moment `$crisp`
 * answers, however late that is. While Crisp is absent (blocked script, dead
 * network) the pill stays hidden: a button that cannot open a chat is worse
 * than no button. The contact page stays reachable through the footer.
 *
 * Crisp events are NOT subscribed to here. Crisp keeps a single callback per
 * event name, so modern-crisp-quiet.js is the one subscriber and re-emits
 * `mb-crisp:opened` / `mb-crisp:closed` / `mb-crisp:message` on `document`;
 * this element listens to those. Post-close focus is handled there as well.
 *
 * Add ?mbcrispdebug=1 to any URL to log decisions to the console.
 */

if (!customElements.get('mb-crisp-launcher')) {
  const debug = new URLSearchParams(location.search).has('mbcrispdebug');
  const log = (...args) => {
    if (debug) console.log('%c[mb-crisp-launcher]', 'color:#c2542f;font-weight:700', ...args);
  };

  const crispIsLive = (value) => typeof value?.is === 'function' && typeof value?.get === 'function';

  const crispHasSession = () => {
    try {
      return Boolean(window.$crisp.get('session:identifier'));
    } catch {
      return false;
    }
  };

  /**
   * TWO different moments, and the difference is the whole point.
   *
   * `mbCrispLive` — l.js has swapped the loader's queue array for the real
   * client object. Commands can be pushed from here, but the session does not
   * exist yet, so an open would sit and wait.
   *
   * `mbCrispUsable` — the session exists, which is when pressing the pill
   * actually opens a window. This is what the pill is revealed on; revealing
   * on `mbCrispLive` put a button on screen seconds before it could do
   * anything, which is worse than no button.
   *
   * Both are watched, never polled. Live comes from an accessor on the global
   * that catches l.js's assignment (and then hands the property back as a
   * plain data property, so Crisp does not run through our getter afterwards).
   * Usable comes from whichever of these lands first:
   *
   *   • `CRISP_READY_TRIGGER`, Crisp's own ready hook. The Shopify app defines
   *     it — that is where postCart/postCustomerID run — so it is wrapped
   *     rather than replaced, and the app's function is called first.
   *   • `mb-crisp:session`, relayed from Crisp's `session:loaded` by
   *     modern-crisp-quiet.js, the single subscriber.
   *   • a session identifier already being there the moment the client goes
   *     live, which is the case on a page the shopper has already visited.
   *
   * If none of them ever lands, both promises stay pending and the pill stays
   * hidden. That is the safe direction: no button beats a dead button.
   */
  let markLive;
  let markUsable;
  const mbCrispLive = new Promise((resolve) => {
    markLive = (how) => {
      log('Crisp live via', how);
      resolve(window.$crisp);
    };
  });
  const mbCrispUsable = new Promise((resolve) => {
    markUsable = (how) => {
      log('Crisp usable via', how);
      resolve(window.$crisp);
    };
  });

  mbCrispLive.then(() => {
    if (crispHasSession()) markUsable('session already open');
  });
  document.addEventListener('mb-crisp:session', () => markUsable('session:loaded'), { once: true });

  if (crispIsLive(window.$crisp)) {
    markLive('already up');
  } else {
    let queue = window.$crisp;
    try {
      Object.defineProperty(window, '$crisp', {
        configurable: true,
        enumerable: true,
        get: () => queue,
        set: (next) => {
          queue = next;
          if (!crispIsLive(next)) return;
          Object.defineProperty(window, '$crisp', {
            value: next,
            writable: true,
            configurable: true,
            enumerable: true,
          });
          markLive('$crisp assignment');
        },
      });
    } catch (error) {
      log('could not watch $crisp:', error.message);
    }
  }

  // Debug only, nothing hangs off it: a chatbox that never comes up otherwise
  // leaves no trace, and the missing pill then looks like our own bug.
  if (debug) {
    const watchdog = setTimeout(() => log('still no Crisp after 15s — pill stays hidden'), 15000);
    mbCrispUsable.then(() => clearTimeout(watchdog));
  }

  let appTrigger = window.CRISP_READY_TRIGGER;
  const wrappedTrigger = function mbCrispReadyTrigger(...args) {
    try {
      if (typeof appTrigger === 'function') appTrigger.apply(this, args);
    } finally {
      markLive('CRISP_READY_TRIGGER');
      markUsable('CRISP_READY_TRIGGER');
    }
  };
  try {
    Object.defineProperty(window, 'CRISP_READY_TRIGGER', {
      configurable: true,
      enumerable: true,
      get: () => wrappedTrigger,
      set: (fn) => {
        appTrigger = fn;
      },
    });
  } catch (error) {
    log('could not wrap CRISP_READY_TRIGGER:', error.message);
  }

  class MBCrispLauncher extends HTMLElement {
    static DEFAULT_PREVIEW_MS = 5000;
    static OPEN_TIMEOUT_MS = 15000;
    static STALE_MESSAGE_MS = 60000;
    static CONNECT_WINDOW_MS = 5000;
    static UNREAD_POLL_MS = 10000;

    #cleanups = [];
    #button = null;
    #status = null;
    #badge = null;
    #preview = null;
    #previewTimer = 0;
    #previewPaused = false;
    #boundAt = 0;

    connectedCallback() {
      this.#button = this.querySelector('[data-mb-crisp-button]');
      this.#status = this.querySelector('[data-mb-crisp-status]');
      this.#badge = this.querySelector('[data-mb-crisp-badge]');
      this.#preview = this.querySelector('[data-mb-crisp-preview]');
      if (!this.#button) return;

      this.#on(this.#button, 'click', () => this.#openChat());

      const reply = this.querySelector('[data-mb-crisp-reply]');
      const dismiss = this.querySelector('[data-mb-crisp-dismiss]');
      if (reply) this.#on(reply, 'click', () => this.#openChat());
      if (dismiss) this.#on(dismiss, 'click', () => this.#hidePreview({ focusButton: true }));

      if (this.#preview) {
        // The clock stops while the shopper is reading or has focus inside —
        // a card that vanishes mid-read is worse than one that lingers.
        this.#on(this.#preview, 'pointerenter', () => this.#pausePreview(true));
        this.#on(this.#preview, 'pointerleave', () => this.#pausePreview(false));
        this.#on(this.#preview, 'focusin', () => this.#pausePreview(true));
        this.#on(this.#preview, 'focusout', (event) => {
          if (!this.#preview.contains(event.relatedTarget)) this.#pausePreview(false);
        });
        this.#on(this.#preview, 'keydown', (event) => {
          if (event.key === 'Escape') this.#hidePreview({ focusButton: true });
        });
      }

      // Bind as soon as commands can be pushed, but only show the pill once a
      // press would actually open something.
      mbCrispLive.then(($crisp) => this.#bind($crisp));
      mbCrispUsable.then(() => this.#reveal());
    }

    disconnectedCallback() {
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
      clearTimeout(this.#previewTimer);
    }

    #on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.#cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    #say(message) {
      if (this.#status) this.#status.textContent = message;
    }

    #push(...command) {
      try {
        window.$crisp.push(command);
      } catch (error) {
        log('push failed:', command, error.message);
      }
    }

    #is(query) {
      try {
        return window.$crisp.is(query) === true;
      } catch {
        return false;
      }
    }

    /**
     * Is the chatbox actually in front of the shopper? `chat:opened` alone is
     * not that question: Crisp keeps it true for the rest of the session once
     * the window has been opened, even while `chat:hide` has the whole client
     * put away. Trusting it suppressed the preview card on every later page —
     * the badge went up with nothing behind it.
     */
    #isChatUp() {
      return this.#is('chat:opened') && this.#is('chat:visible');
    }

    /** A message the chatbox is re-showing from history, not one just sent. */
    #isReplay(message) {
      if (message?.origin !== 'history') return false;
      const sentAt = Number(message?.timestamp);
      if (Number.isFinite(sentAt) && sentAt > 0) {
        return Date.now() - sentAt > MBCrispLauncher.STALE_MESSAGE_MS;
      }
      return Date.now() - this.#boundAt < MBCrispLauncher.CONNECT_WINDOW_MS;
    }

    /** Everything that follows the window opening, however it opened. */
    #markOpened() {
      this.#setOpen(true);
      this.#hidePreview();
      // Crisp marks the conversation read as it opens.
      this.#setUnread(0);
    }

    /** Idempotent: the pill only ever goes from hidden to shown. */
    #reveal() {
      this.hidden = false;
    }

    async #openChat() {
      if (this.hasAttribute('data-mb-loading')) return;

      // Nothing here waits on the network: the pill is only visible after
      // Crisp answered, so `mbCrispUsable` is already settled by the time a
      // shopper can press. It never rejects, so there is nothing to catch.
      this.#setLoading(true);
      await mbCrispUsable;
      this.#hidePreview();
      this.#push('do', 'chat:show');
      this.#push('do', 'chat:open');
      // The client answers before its chat window is downloadable, and the
      // first open also creates the session, so the press can sit for seconds.
      // Hold the spinner until the window is actually up, or the shopper is
      // left pressing a button that looks broken.
      await this.#whenChatWindowUp();
      // Crisp emits `chat:opened` only for opens made through its own bubble
      // (measured 2026-09-02) — a programmatic open is silent. Without this the
      // pill stayed up beside the open window.
      this.#markOpened();
      this.#say('');
      this.#setLoading(false);
    }

    /**
     * Resolves when Crisp's chat window is on screen. Watched, not polled: a
     * MutationObserver on the body sees the dialog arrive. The timeout only
     * releases the spinner — it never hides the pill or opens anything.
     */
    #whenChatWindowUp() {
      const up = () => {
        const dialog = document.querySelector('.crisp-client[role="dialog"], .crisp-client [role="dialog"]');
        return Boolean(dialog) && this.#is('chat:visible');
      };
      if (up()) return Promise.resolve();

      return new Promise((resolve) => {
        const finish = () => {
          observer.disconnect();
          clearTimeout(timer);
          // Drop our own cleanup entry: without this the list grew by one on
          // every press and nothing ever removed the dead ones.
          const at = this.#cleanups.indexOf(finish);
          if (at !== -1) this.#cleanups.splice(at, 1);
          resolve();
        };
        const observer = new MutationObserver(() => {
          if (up()) finish();
        });
        observer.observe(document.body, { childList: true, subtree: true, attributes: true });
        const timer = setTimeout(() => {
          log('chat window never appeared — releasing the spinner');
          finish();
        }, MBCrispLauncher.OPEN_TIMEOUT_MS);
        this.#cleanups.push(finish);
      });
    }

    #setLoading(loading) {
      this.toggleAttribute('data-mb-loading', loading);
      this.#button.setAttribute('aria-busy', String(loading));
      if (loading) this.#say('Connecting to support…');
    }

    #bind($crisp) {
      log('bound');
      this.#boundAt = Date.now();

      // Crisp's own bubble goes away for good; our pill is the launcher now.
      // Only while closed — hiding an open box would pull it out from under
      // a shopper who arrived mid-conversation with the box restored open.
      if (this.#isChatUp()) this.#setOpen(true);
      else this.#push('do', 'chat:hide');

      this.#on(document, 'mb-crisp:opened', () => this.#markOpened());

      this.#on(document, 'mb-crisp:closed', () => {
        this.#setOpen(false);
        this.#push('do', 'chat:hide');
        this.#syncUnread();
      });

      this.#on(document, 'mb-crisp:message', (event) => {
        const message = event.detail;
        log('message received:', message);
        // `origin` alone cannot tell a replay from a live message: the chatbox
        // replays its history as `origin: "history"` when it connects, but a
        // workflow message written seconds ago arrives with that same origin —
        // filtering on it swallowed the card entirely. The message carries its
        // own timestamp, so ask how OLD it is rather than when it turned up.
        // The connect window is only the fallback for a payload without one.
        if (!this.#isChatUp() && !this.#isReplay(message)) this.#showPreview(message);
        this.#syncUnread();
      });

      // Unread from a previous page shows as a badge, never as a card: the
      // card belongs to a message arriving while the shopper is here. The
      // count reads 0 until Crisp's session loads (measured: `get` said 0
      // while the same call said 1 a second later), so it is re-read on
      // `session:loaded`.
      this.#syncUnread();
      this.#syncAvailability();
      this.#on(document, 'mb-crisp:session', () => {
        this.#syncUnread();
        this.#syncAvailability();
      });
      this.#on(document, 'mb-crisp:availability', () => this.#syncAvailability());

      // The only poll left in this file, and it earns its keep: a burst of
      // agent messages once slipped past message:received (the badge stuck on
      // 3 while Crisp's own getter said 6). Local reads, no network.
      const poll = setInterval(() => {
        this.#syncUnread();
        this.#syncAvailability();
      }, MBCrispLauncher.UNREAD_POLL_MS);
      this.#cleanups.push(() => clearInterval(poll));
    }

    #setOpen(open) {
      this.toggleAttribute('data-mb-chat-open', open);
      this.#button.setAttribute('aria-expanded', String(open));
    }

    #syncAvailability() {
      const available = this.#is('website:available');
      this.toggleAttribute('data-mb-offline', !available);
      log('availability:', available);
    }

    #unreadCount() {
      try {
        return Number(window.$crisp.get('chat:unread:count')) || 0;
      } catch {
        return 0;
      }
    }

    #syncUnread() {
      this.#setUnread(this.#unreadCount());
    }

    #setUnread(count) {
      if (!this.#badge) return;
      this.#badge.textContent = count > 9 ? '9+' : String(count);
      this.#badge.hidden = count === 0;
      this.toggleAttribute('data-mb-unread', count > 0);
      this.#button.setAttribute(
        'aria-label',
        count > 0 ? `Need help? ${count} unread ${count === 1 ? 'message' : 'messages'}` : 'Need help?',
      );
      log('unread:', count);
    }

    /** One line of copy for a message the card can't render verbatim. */
    static #describe(message) {
      const type = message?.type ?? 'text';
      const content = message?.content;
      if (type === 'text' && typeof content === 'string') return content;
      if (type === 'file') return content?.name ? `Sent a file: ${content.name}` : 'Sent a file';
      if (type === 'animation') return 'Sent a GIF';
      if (type === 'audio') return 'Sent a voice message';
      if (type === 'picker' || type === 'field' || type === 'carousel') return content?.text ?? 'Sent a message';
      return typeof content === 'string' ? content : 'Sent a message';
    }

    #showPreview(message) {
      if (!this.#preview) return;

      const nickname = message?.user?.nickname || 'Support';
      const avatar = message?.user?.avatar;
      const text = MBCrispLauncher.#describe(message);

      this.querySelector('[data-mb-crisp-name]').textContent = nickname;
      this.querySelector('[data-mb-crisp-text]').textContent = text;

      const avatarEl = this.querySelector('[data-mb-crisp-avatar]');
      if (avatarEl) {
        avatarEl.replaceChildren();
        if (avatar) {
          const img = document.createElement('img');
          img.src = avatar;
          img.alt = '';
          avatarEl.appendChild(img);
        } else {
          avatarEl.textContent = nickname.trim().charAt(0).toUpperCase();
        }
      }

      this.#preview.hidden = false;
      this.toggleAttribute('data-mb-preview', true);
      this.#say(`New message from ${nickname}: ${text}`);
      this.#armPreviewTimer();
    }

    #hidePreview({ focusButton = false } = {}) {
      if (!this.#preview || this.#preview.hidden) return;
      clearTimeout(this.#previewTimer);
      this.#previewPaused = false;

      // Never hide the element holding focus without handing it on.
      if (focusButton || this.#preview.contains(document.activeElement)) {
        this.#button.focus({ preventScroll: true });
      }
      this.#preview.hidden = true;
      this.toggleAttribute('data-mb-preview', false);
    }

    #previewTimeout() {
      const raw = Number(this.dataset.previewTimeout);
      return Number.isFinite(raw) ? raw : MBCrispLauncher.DEFAULT_PREVIEW_MS;
    }

    #armPreviewTimer() {
      clearTimeout(this.#previewTimer);
      const ms = this.#previewTimeout();
      if (ms <= 0 || this.#previewPaused) return;
      this.#previewTimer = setTimeout(() => this.#hidePreview(), ms);
    }

    #pausePreview(paused) {
      this.#previewPaused = paused;
      if (paused) clearTimeout(this.#previewTimer);
      else this.#armPreviewTimer();
    }
  }

  customElements.define('mb-crisp-launcher', MBCrispLauncher);
}
